import { Button, Popconfirm, Tabs, TabsProps, Select, Spin, Space, Tooltip, message, InputNumber, Progress } from "antd";
import { DownloadOutlined, LoadingOutlined, CheckCircleOutlined, SyncOutlined, EditOutlined, DownOutlined, UpOutlined, ScissorOutlined, CopyOutlined } from "@ant-design/icons";
import { FaCheckCircle, FaCircle } from "react-icons/fa";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import plaid from "../../../../assets/images/plaid.svg";
import styles from "./group-data.module.scss";
import GroupTab from "./tabs";
import SplitApplicationModal from "./SplitApplicationModal";
import { Loader } from "../../../../components";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { setProperty } from "../../../../store/properties/property-slice";
import {
  asyncGetAppGroupPlaidResults,
  asyncGetAppGroupScreeningRequestData,
  asyncGetApplicationGroupScreening,
  asyncGetApplicationsByGroup,
  asyncGetPropertyUnitRent,
  asyncGetPropertyUnits,
  asyncRequestApplicationGroupScreening,
  asyncUpdateAppInfoService,
  asyncRefreshIDVForGroup,
  asyncGetApplicationGroups,
} from "../../../../store/easyApp/easyapp-async";
import { clearGroupData, setSelectedAppGroupId } from "../../../../store/easyApp/easyapp-slice";
import {
  formatApplicantNames,
  formatDate,
  getFormattedDate,
  generateGroupPDF,
  getColorFromRecommendation,
  getColorFromApplicantRecommendation,
} from "../easyapp.utils";
import { getRentalVerificationFormService, updateApplicationDataService } from "../../../../services/easyapp.service";
import { config } from "../../../../utils/config";

const GroupData = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingRent, setLoadingRent] = useState<boolean>(false);
  const [buttonStates, setButtonStates] = useState({
    submittingRequest: false,
    fetchingResults: false,
  });
  const [screeningState, setScreeningState] = useState<{
    hasBeenSubmitted: boolean;
    hasResults: boolean;
    lastRequestDate: Date | null;
  }>({
    hasBeenSubmitted: false,
    hasResults: false,
    lastRequestDate: null,
  });
  const [error, setError] = useState<{
    application: boolean;
    submission: boolean;
    fetching: boolean;
  }>({
    application: false,
    submission: false,
    fetching: false,
  });
  const [loadingTab, setLoadingTab] = useState<boolean>(false);
  const [selectedUnit, setSelectedUnit] = useState();
  const [selectedProperty, setSelectedProperty] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [manualIncomeValues, setManualIncomeValues] = useState<Record<string, number | null>>({});
  const [savingManualIncome, setSavingManualIncome] = useState<Record<string, boolean>>({});
  const [expandedIncomeInputs, setExpandedIncomeInputs] = useState<Record<string, boolean>>({});
  const [screeningProgress, setScreeningProgress] = useState<{
    isActive: boolean;
    step: number;
    stepText: string;
    elapsedSeconds: number;
  }>({
    isActive: false,
    step: 0,
    stepText: "",
    elapsedSeconds: 0,
  });
  const [refreshingIDV, setRefreshingIDV] = useState(false);
  const [splitModalVisible, setSplitModalVisible] = useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.user);
  const easyapp = useAppSelector((state) => state.easyapp);
  const { userProperty }: any = useAppSelector((state) => state.user);

  const {
    selectedAppGroupId,
    selectedAppGroup,
    selectedAppGroupPlaidResults,
    selectedAppGroupScreening,
    selectedPropertyUnits,
    selectedPropertyUnitRent,
  } = easyapp || {};

  const { Option } = Select;

  useEffect(() => {
    if (selectedAppGroupScreening) {
      const hasResults = selectedAppGroupScreening.Applicants?.length > 0;
      const hasBeenSubmitted = !!selectedAppGroupScreening.RequestDate;
      const lastRequestDate = selectedAppGroupScreening.RequestDate
        ? new Date(selectedAppGroupScreening.RequestDate)
        : null;

      setScreeningState({
        hasBeenSubmitted,
        hasResults,
        lastRequestDate,
      });
    }
  }, [selectedAppGroupScreening]);

  // Initial data fetch
  useEffect(() => {
    let isMounted = true;

    async function fetchApplicationData() {
      if (!user) return;

      let groupId = selectedAppGroupId;
      if (!groupId) {
        const storedId = sessionStorage.getItem("selectedAppGroupId");
        if (storedId) {
          groupId = storedId;
          dispatch(setSelectedAppGroupId(storedId));
        } else {
          navigate("/products/EasyApp");
          return;
        }
      }

      try {
        setLoading(true);
        await Promise.all([
          dispatch(asyncGetApplicationsByGroup(groupId)).unwrap(),
          dispatch(asyncGetAppGroupPlaidResults(groupId)).unwrap(),
          dispatch(asyncGetApplicationGroupScreening(groupId)).unwrap(),
        ]);
      } catch (error) {
        if (isMounted) setError((prev) => ({ ...prev, application: true }));
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchApplicationData();
    return () => {
      isMounted = false;
    };
  }, [dispatch, user, selectedAppGroupId, navigate]);

  useEffect(() => {
    if (!selectedAppGroup) return;

    let isMounted = true;

    async function fetchPropertyUnits() {
      try {
        setLoadingRent(true);
        setSelectedProperty(selectedAppGroup.ms_property_id);
        setSelectedUnit(selectedAppGroup.desired_unit_number);
        await Promise.all([
          dispatch(asyncGetPropertyUnits(selectedAppGroup.ms_property_id)),
          dispatch(
            asyncGetPropertyUnitRent({
              id: selectedAppGroup.ms_property_id,
              unitId: selectedAppGroup.desired_apartment,
            })
          ),
        ]);
      } catch (error) {
        console.error(error);
      } finally {
        if (isMounted) setLoadingRent(false);
      }
    }

    fetchPropertyUnits();

    return () => {
      isMounted = false;
    };
  }, [dispatch, selectedAppGroup]);  

  useEffect(() => {
    dispatch(
      setProperty({
        tabs: ["EasyApp"],
      })
    );
  }, [dispatch]);

  if (loading) {
    return <Loader />;
  }

  if (!selectedAppGroup || !selectedAppGroup.applicants) {
    return (
      <div className={styles.errorState}>
        <h3>Unable to load application data</h3>
        <p>There was a problem retrieving the application information.</p>
        <Button type="primary" onClick={() => navigate("/products/EasyApp")}>
          Return to applications
        </Button>
      </div>
    );
  }

  const {
    applicants,
    applicant_names = [],
    desired_lease_term = "N/A",
    desired_move_in_date,
  } = selectedAppGroup;

  const handleSubmitScreeningRequest = async () => {
    let timerInterval: NodeJS.Timeout | null = null;
    
    try {
      setButtonStates((prev) => ({ ...prev, submittingRequest: true }));
      setError((prev) => ({ ...prev, submission: false }));
      
      // Start the screening progress overlay
      setScreeningProgress({
        isActive: true,
        step: 1,
        stepText: "Preparing applicant data...",
        elapsedSeconds: 0,
      });

      // Start elapsed time counter
      timerInterval = setInterval(() => {
        setScreeningProgress((prev) => ({
          ...prev,
          elapsedSeconds: prev.elapsedSeconds + 1,
        }));
      }, 1000);

      const reqDataResponse = await dispatch(
        asyncGetAppGroupScreeningRequestData()
      );
      const reqData: any = reqDataResponse?.payload;

      if (!reqData || !reqData.PropertyId) {
        setError((prev) => ({ ...prev, submission: true }));
        message.error("Transunion Property Id is missing for this property in our records");
        setScreeningProgress((prev) => ({ ...prev, isActive: false }));
        return;
      }

      // Update progress
      setScreeningProgress((prev) => ({
        ...prev,
        step: 2,
        stepText: "Submitting to TransUnion...",
      }));

      const response = await dispatch(
        asyncRequestApplicationGroupScreening(reqData)
      );

      if (response.payload.success === false) {
        setError((prev) => ({ ...prev, submission: true }));
        setScreeningProgress((prev) => ({ ...prev, isActive: false }));
        return;
      }

      // Update progress - waiting for results
      setScreeningProgress((prev) => ({
        ...prev,
        step: 3,
        stepText: "Waiting for TransUnion response...",
      }));

      // Fetch results after successful submission
      if (selectedAppGroupId) {
        setScreeningProgress((prev) => ({
          ...prev,
          step: 4,
          stepText: "Fetching screening results...",
        }));
        
        await dispatch(asyncGetApplicationGroupScreening(selectedAppGroupId));
        
        // Success!
        setScreeningProgress((prev) => ({
          ...prev,
          step: 5,
          stepText: "Screening complete!",
        }));
        
        // Keep success state visible briefly
        setTimeout(() => {
          setScreeningProgress({
            isActive: false,
            step: 0,
            stepText: "",
            elapsedSeconds: 0,
          });
        }, 1500);
      }

      return;
    } catch (error) {
      setError((prev) => ({ ...prev, submission: true }));
      setScreeningProgress((prev) => ({ ...prev, isActive: false }));
    } finally {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
      setButtonStates((prev) => ({ ...prev, submittingRequest: false }));
    }
  };

  const handleFetchResults = async () => {
    if (!selectedAppGroupId) return;

    try {
      setButtonStates((prev) => ({ ...prev, fetchingResults: true }));
      setError((prev) => ({ ...prev, fetching: false }));

      await dispatch(asyncGetApplicationGroupScreening(selectedAppGroupId));
      
    } catch (error) {
      setError((prev) => ({ ...prev, fetching: true }));
    } finally {
      setButtonStates((prev) => ({ ...prev, fetchingResults: false }));
    }
  };

  const handleTabClick = () => {
    setLoadingTab(true);
    setTimeout(() => {
      setLoadingTab(false);
    }, 250);
  };

  const handleGeneratePDF = () => {
    generateGroupPDF(
      selectedAppGroup,
      selectedAppGroupPlaidResults,
      selectedAppGroupScreening
    );
  };

  const handleSaveManualIncome = async (applicationId: string, value: number | null) => {
    if (!applicationId) {
      message.error("Application ID not found");
      return;
    }
    
    try {
      setSavingManualIncome(prev => ({ ...prev, [applicationId]: true }));
      const token = config.REACT_APP_CREDIBUILDER_SECRET;
      
      await updateApplicationDataService(
        {
          applicationId,
          fieldName: 'manual_income',
          value: value,
        },
        token
      );
      
      message.success("Manual income saved successfully");
      
      // Clear the input field
      setManualIncomeValues(prev => {
        const updated = { ...prev };
        delete updated[applicationId];
        return updated;
      });
      
      // Refresh the application group data to get updated values
      if (selectedAppGroupId) {
        await dispatch(asyncGetApplicationsByGroup(selectedAppGroupId));
      }
    } catch (error) {
      console.error("Error saving manual income:", error);
      message.error("Failed to save manual income");
    } finally {
      setSavingManualIncome(prev => ({ ...prev, [applicationId]: false }));
    }
  };

  const { data = [] } = selectedAppGroupPlaidResults || {};

  const formattedApplicants = formatApplicantNames(applicant_names);

  const renderSubmitButton = () => {
    // Calculate income qualification for button state
    const totalManualIncome = applicants?.reduce((sum: number, applicant: any) => {
      return sum + (applicant?.manual_income || 0);
    }, 0) || 0;
    const plaidIncome = data?.groupMonthlyIncome || 0;
    const totalGroupIncome = plaidIncome + totalManualIncome;
    const hasManualIncome = totalManualIncome > 0;
    const rentAmount = selectedAppGroup?.leaseDraft?.rent || selectedPropertyUnitRent || 0;
    
    // Check if selected unit is an affordable unit
    const selectedUnitObj = selectedPropertyUnits?.find(
      (unit: any) => unit.unitNumber === selectedAppGroup?.desired_unit_number
    );
    const isAffordableUnit = selectedUnitObj?.isAffordableUnit || false;
    
    // Affordable units: 1.5x net or 2.0x gross
    // Regular units: 1.7x net or 2.5x gross
    const incomeMultiple = isAffordableUnit 
      ? (hasManualIncome ? 2.0 : 1.5)
      : (hasManualIncome ? 2.5 : 1.7);
    const requiredIncome = rentAmount * incomeMultiple;
    const meetsIncomeRequirement = rentAmount === 0 || totalGroupIncome >= requiredIncome;

    // Check ID verification status for each applicant
    // Uses explicit user ID matching for reliability (falls back to index if not available)
    const getIdVerificationStatus = () => {
      return applicants?.map((applicant: any, index: number) => {
        // Try to match by user ID first, then fall back to index
        const userId = applicant?.user;
        const idvData = userId && data?.idvByUserId?.[userId] 
          ? data.idvByUserId[userId] 
          : data?.applicantsIdv?.[index];
        const isPlaidVerified = idvData?.status === "success";
        const isManuallyVerified = applicant?.id_manually_verified === true;
        return {
          name: applicant?.first_name || `Applicant ${index + 1}`,
          isVerified: isPlaidVerified || isManuallyVerified,
          verificationMethod: isPlaidVerified ? "Plaid" : isManuallyVerified ? "Manual" : null,
        };
      }) || [];
    };

    const idVerificationStatuses = getIdVerificationStatus();
    const allIdsVerified = idVerificationStatuses.every((s: any) => s.isVerified);

    const buttonDisabled = buttonStates.submittingRequest || !meetsIncomeRequirement;

    const renderButtonContent = () => {
      if (buttonStates.submittingRequest) {
        return "Submitting Transunion screening...";
      }
      if (!meetsIncomeRequirement) {
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            Income requirement not met
          </div>
        );
      }
      if (screeningState.hasBeenSubmitted) {
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <FaCheckCircle style={{ marginRight: "6px" }} />
            Transunion screening submitted, Rescreen?
          </div>
        );
      }
      if (error.submission) {
        return "Error submitting screening - Try again";
      }
      return "Start Transunion screening";
    };

    const button = (
      <Button
        type="default"
        loading={buttonStates.submittingRequest}
        disabled={buttonDisabled}
        className={`${styles.animatedButton} ${
          buttonStates.submittingRequest ? styles.downloading : ""
        }`}
        style={{
          background: !meetsIncomeRequirement
            ? "#f5f5f5"
            : screeningState.hasBeenSubmitted
            ? "#DEE4ED"
            : "#2D394A",
          color: !meetsIncomeRequirement
            ? "#999"
            : screeningState.hasBeenSubmitted
            ? "#323a46"
            : "#fff",
          fontWeight: "500",
          fontSize: "16px",
          fontFamily: "Lato",
          borderRadius: "4px",
          cursor: !meetsIncomeRequirement ? "not-allowed" : "pointer",
        }}
        size="large"
      >
        {renderButtonContent()}
      </Button>
    );

    // If income requirement not met, show tooltip explaining why button is disabled
    if (!meetsIncomeRequirement) {
      return (
        <Tooltip
          title={
            <div>
              <strong>Cannot start screening</strong>
              <br />
              Group income (${totalGroupIncome.toLocaleString('en-US', { minimumFractionDigits: 2 })}/mo) 
              is below the required ${requiredIncome.toLocaleString('en-US', { minimumFractionDigits: 2 })}/mo 
              ({incomeMultiple}x rent).
            </div>
          }
        >
          {button}
        </Tooltip>
      );
    }

    // If income requirement is met, show Popconfirm with warnings
    return (
      <Popconfirm
        title="Before Starting Screening"
        description={
          <div
            style={{
              maxWidth: "340px",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            <p style={{ marginBottom: "12px", fontWeight: 500 }}>
              Please confirm the following before proceeding:
            </p>
            <ul style={{ paddingLeft: "18px", margin: 0 }}>
              <li style={{ marginBottom: "8px" }}>
                <strong>All applicants</strong> who will be on the lease are present in this application group
              </li>
              <li style={{ marginBottom: "8px" }}>
                <strong>Every applicant's ID</strong> has been verified (either by Plaid or manually verified)
              </li>
            </ul>
            {!allIdsVerified && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "8px",
                  background: "#fff7e6",
                  border: "1px solid #ffd591",
                  borderRadius: "4px",
                  fontSize: "13px",
                }}
              >
                <strong>⚠️ Warning:</strong> Some applicants may not have verified IDs:
                <ul style={{ paddingLeft: "16px", margin: "4px 0 0 0" }}>
                  {idVerificationStatuses
                    .filter((s: any) => !s.isVerified)
                    .map((s: any, i: number) => (
                      <li key={i}>{s.name} - ID not verified</li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        }
        onConfirm={() => {
          setTimeout(() => handleSubmitScreeningRequest(), 0);
        }}
        okText="Yes, Proceed with Screening"
        cancelText="Cancel"
      >
        {button}
      </Popconfirm>
    );
  };

  const renderFetchButton = () => {
    const showButton = screeningState.hasBeenSubmitted;

    if (!showButton) return null;

    return (
      <Button
        type="default"
        loading={buttonStates.fetchingResults}
        onClick={handleFetchResults}
        disabled={buttonStates.fetchingResults}
        className={`${styles.animatedButton} ${
          buttonStates.fetchingResults ? styles.downloading : ""
        }`}
        style={{
          background: screeningState.hasResults ? "#DEE4ED" : "#2D394A",
          color: screeningState.hasResults ? "#323a46" : "#fff",
          fontWeight: "500",
          fontSize: "16px",
          fontFamily: "Lato",
          borderRadius: "4px",
        }}
        size="large"
      >
        {buttonStates.fetchingResults ? (
          "Fetching results..."
        ) : screeningState.hasResults ? (
          <div style={{ display: "flex", alignItems: "center" }}>
            <FaCheckCircle style={{ marginRight: "6px" }} />
            Refresh results
          </div>
        ) : error.fetching ? (
          "Error fetching results - Try again"
        ) : (
          "Get Transunion results"
        )}
      </Button>
    );
  };

  const renderStatusMessage = () => {
    const hasValidData = (array: any[]) =>
      array?.length > 0 && array.some((item) => item && !item.error);

    const isPlaidVerified = !!(
      hasValidData(data.applicantsIdv) &&
      (hasValidData(data.applicantsIncome) ||
      hasValidData(data.applicantsBankIncome))
    );
    const formattedTimestamp = screeningState.lastRequestDate
      ? getFormattedDate(screeningState.lastRequestDate)
      : "";

    if (error.submission) {
      return "Request for screening failed. Try again!";
    }

    if (buttonStates.submittingRequest) {
      return `Submitting ${formattedTimestamp}`;
    }

    if (screeningState.hasBeenSubmitted && screeningState.lastRequestDate) {
      const status = screeningState.hasResults ? "Received" : "Submitted";
      return `${status} on ${formattedTimestamp}`;
    }

    if (isPlaidVerified && !error.application) {
      return "Ready to Screen with TransUnion";
    }

    return "Data has not been verified by Plaid";
  };

  const tabItems: TabsProps["items"] = applicant_names.map(
    (name: any, index: number) => {
      const firstName = name.first_name?.trim();
      const lastName = name.last_name?.trim();
      const hasName = firstName || lastName;
      
      // Get applicant data and match IDV by user ID
      const applicant = applicants[index];
      const userId = applicant?.user;
      const applicantIdv = userId && data?.idvByUserId?.[userId] 
        ? data.idvByUserId[userId] 
        : data?.applicantsIdv?.[index] || null;
      const applicantIncome = userId && data?.incomeByUserId?.[userId]
        ? data.incomeByUserId[userId]
        : null;

      return {
        key: String(index + 1),
        label: hasName
          ? `${firstName || ""} ${lastName || ""}`.trim()
          : `Unknown`,
        children: (
          <GroupTab
            appGroupId={selectedAppGroupId}
            applicants={applicant}
            applicantIdv={applicantIdv}
            applicantPayrollIncome={applicantIncome?.plaidIncomeData || data?.applicantsIncome?.[index] || null}
            applicantBankIncome={applicantIncome?.plaidBankIncomeData || data?.applicantsBankIncome?.[index] || null}
            loading={loadingTab}
            screening={selectedAppGroupScreening?.Applicants?.[index] || {}}
            screeningResults={selectedAppGroupScreening || {}}
          />
        ),
      };
    }
  );

  const RecommendationField = ({
    title,
    screening_data,
    applicant_names,
    get_recommendation,
    useApplicationLevel = false,
    tooltip,
  }: {
    title: string;
    screening_data: any;
    applicant_names: any;
    get_recommendation: any;
    useApplicationLevel?: boolean;
    tooltip?: React.ReactNode;
  }) => {
    const renderPendingState = () => (
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <FaCircle
          style={{
            color: getColorFromRecommendation(undefined),
            height: "12px",
          }}
        />
        <p>Not-Started</p>
      </div>
    );

    const renderApplicationLevel = () => {
      if (!screening_data?.ScoreResult) {
        return renderPendingState();
      }

      const { recommendation, message } = get_recommendation(
        screening_data.ScoreResult
      );

      return (
        <p style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <FaCircle
            style={{ color: getColorFromRecommendation(recommendation) }}
          />{" "}
          {message || "Pending"}
        </p>
      );
    };

    const renderApplicantLevel = () => {
      if (!screening_data?.Applicants?.length) {
        return renderPendingState();
      }

      return screening_data.Applicants.map((screening: any, index: any) => {
        const { message } = get_recommendation(screening);
        const applicantName =
          applicant_names?.[index]?.first_name || `Applicant ${index + 1}`;

        return (
          <p
            key={index}
            style={{ display: "flex", alignItems: "center", gap: "10px" }}
          >
            <FaCircle
              style={{ color: getColorFromApplicantRecommendation(message) }}
            />{" "}
            {message || "Pending"} for {applicantName}
          </p>
        );
      });
    };

    return (
      <div>
        {tooltip ? (
          <Tooltip title={tooltip}>
            <p className={styles.detail} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              {title}
            </p>
          </Tooltip>
        ) : (
          <p className={styles.detail}>{title}</p>
        )}
        {screening_data
          ? useApplicationLevel
            ? renderApplicationLevel()
            : renderApplicantLevel()
          : renderPendingState()}
      </div>
    );
  };

  const ValueField = ({
    title,
    screening_data,
    applicant_names,
    getValue,
  }: {
    title: string;
    screening_data: any;
    applicant_names: any;
    getValue: any;
  }) => {
    return (
      <div>
        <p className={styles.detail}>{title}</p>
        {screening_data?.Applicants?.length > 0 ? (
          screening_data.Applicants.map((screening: any, index: number) => (
            <p
              key={index}
              style={{ display: "flex", alignItems: "center", gap: "10px" }}
            >
              <FaCircle
                style={{
                  color: getColorFromRecommendation(
                    (screening.ScoreResult.ApplicantScore) ? 1 : 7
                  ),
                  height: "12px",
                }}
              />{" "}
              {getValue(screening) || "N/A"} for{" "}
              {applicant_names?.[index]?.first_name || "Unknown"}
            </p>
          ))
        ) : (
          <p style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <FaCircle
              style={{
                color: getColorFromRecommendation(undefined),
                height: "12px",
              }}
            />{" "}
            Not-Started
          </p>
        )}
      </div>
    );
  };

  const handleChange = async(type: "property" | "apartment", value: any) => {
    try {
      if (!selectedAppGroup) return;

      if (type === "property") {
        setSelectedProperty(value);
        
        const property_name = (userProperty || []).find((p: any) => p.property_id === value)?.property_name;

        await dispatch(
          asyncUpdateAppInfoService({
            fieldName: "ms_property_id",
            value,
            application_group: selectedAppGroup.application_group,
            property_name: property_name
          })
        );
        
        await dispatch(asyncGetApplicationsByGroup(selectedAppGroup.application_group)).unwrap();
        setSelectedUnit(undefined);
      }

      if (type === "apartment") {
        setSelectedUnit(value);

        const selectedUnitObj = selectedPropertyUnits.find(
          (unit: any) => unit.unitNumber === value
        );

        if (selectedUnitObj) {
          dispatch(
            asyncUpdateAppInfoService({
              fieldName: "desired_apartment",
              value: selectedUnitObj.unitId,
              application_group: selectedAppGroup.application_group,
              unitNumber: selectedUnitObj.unitNumber,
            })
          );
        }
      }
    } catch (error) {
      throw error;
    }
  };

  const propertyOptions = () => {
      return (userProperty || []).map((property: any) => ({
        value: property.property_id,
        label: property.property_name,
      }));
  };
  
  const handleVerificationForm = async () => {
    setDownloading(true);

    try {
      const applicants = selectedAppGroup?.applicants || [];
      if (applicants.length === 0) {
        message.warning("No applicants selected for the verification form.");
        return;
      }

      const body = applicants.map((a: any) => ({
        name: `${a.first_name} ${a.last_name}`,
        userId: a.user,
      }));

      const response = await getRentalVerificationFormService(body);

      if (!response?.data) {
        throw new Error("Received empty response from server.");
      }

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "Rental_Verification_Form.pdf";

      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);

      message.success("Verification form downloaded successfully!");
    } catch (err) {
      message.error(
        "Failed to download the verification form. Please try again."
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleRefreshIDV = async () => {
    if (!selectedAppGroupId) return;
    
    try {
      setRefreshingIDV(true);
      const result = await dispatch(asyncRefreshIDVForGroup(selectedAppGroupId)).unwrap();
      
      if (result?.success) {
        message.success(`IDV refreshed for ${result.successCount} of ${result.totalUsers} applicants`);
        // Refresh Plaid results to get the updated IDV data
        await dispatch(asyncGetAppGroupPlaidResults(selectedAppGroupId));
      } else {
        message.warning(result?.error || 'Failed to refresh some IDV records');
      }
    } catch (error) {
      console.error('Error refreshing IDV:', error);
      message.error('Failed to refresh IDV data. Please try again.');
    } finally {
      setRefreshingIDV(false);
    }
  };  

  return (
    <div className={styles.container}>
      {/* Screening Progress Overlay */}
      {screeningProgress.isActive && (
        <div className={styles.screeningOverlay}>
          <div className={styles.screeningModal}>
            <div className={styles.screeningHeader}>
              {screeningProgress.step < 5 ? (
                <SyncOutlined spin className={styles.screeningSpinner} />
              ) : (
                <CheckCircleOutlined className={styles.screeningSuccess} />
              )}
              <h3>
                {screeningProgress.step < 5
                  ? "TransUnion Screening in Progress"
                  : "Screening Complete!"}
              </h3>
            </div>

            <div className={styles.screeningSteps}>
              <div className={`${styles.screeningStep} ${screeningProgress.step >= 1 ? styles.active : ""} ${screeningProgress.step > 1 ? styles.completed : ""}`}>
                <div className={styles.stepIcon}>
                  {screeningProgress.step > 1 ? <CheckCircleOutlined /> : screeningProgress.step === 1 ? <LoadingOutlined /> : <span>1</span>}
                </div>
                <span>Preparing applicant data</span>
              </div>
              <div className={`${styles.screeningStep} ${screeningProgress.step >= 2 ? styles.active : ""} ${screeningProgress.step > 2 ? styles.completed : ""}`}>
                <div className={styles.stepIcon}>
                  {screeningProgress.step > 2 ? <CheckCircleOutlined /> : screeningProgress.step === 2 ? <LoadingOutlined /> : <span>2</span>}
                </div>
                <span>Submitting to TransUnion</span>
              </div>
              <div className={`${styles.screeningStep} ${screeningProgress.step >= 3 ? styles.active : ""} ${screeningProgress.step > 3 ? styles.completed : ""}`}>
                <div className={styles.stepIcon}>
                  {screeningProgress.step > 3 ? <CheckCircleOutlined /> : screeningProgress.step === 3 ? <LoadingOutlined /> : <span>3</span>}
                </div>
                <span>Waiting for response</span>
              </div>
              <div className={`${styles.screeningStep} ${screeningProgress.step >= 4 ? styles.active : ""} ${screeningProgress.step > 4 ? styles.completed : ""}`}>
                <div className={styles.stepIcon}>
                  {screeningProgress.step > 4 ? <CheckCircleOutlined /> : screeningProgress.step === 4 ? <LoadingOutlined /> : <span>4</span>}
                </div>
                <span>Fetching results</span>
              </div>
            </div>

            <div className={styles.screeningStatus}>
              <p className={styles.currentStep}>{screeningProgress.stepText}</p>
              <p className={styles.elapsedTime}>
                Time elapsed: {screeningProgress.elapsedSeconds}s
                {screeningProgress.step < 5 && screeningProgress.elapsedSeconds < 30 && (
                  <span className={styles.timeNote}> (typically takes 15-30 seconds)</span>
                )}
              </p>
            </div>

            {screeningProgress.step < 5 && (
              <Progress
                percent={Math.min((screeningProgress.step / 5) * 100 + (screeningProgress.elapsedSeconds % 10) * 2, 95)}
                showInfo={false}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
                className={styles.screeningProgressBar}
              />
            )}
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.content} style={{ marginBottom: "15px" }}>
          <p
            onClick={() => {
              sessionStorage.removeItem("selectedAppGroupId");
              navigate("/products/EasyApp");
              dispatch(clearGroupData());
            }}
          >
            Back to list
          </p>
          <h3 className={styles.heading}>{formattedApplicants}</h3>
        </div>

        {/* Screening Button */}
        <div
          style={{ display: "flex", flexDirection: "column", textAlign: "end" }}
        >
          <div style={{ display: "flex", gap: 10 }}>
            <div className={styles.screeningSection}>
              <Space size="small">
                {/* Split Applicant Button - only show if there are 2+ applicants */}
                {applicants?.length > 1 && (
                  <Tooltip title="Split an applicant into their own application group">
                    <Button
                      type="default"
                      size="large"
                      icon={<ScissorOutlined />}
                      onClick={() => setSplitModalVisible(true)}
                      style={{
                        fontWeight: "500",
                        fontSize: "16px",
                        fontFamily: "Lato",
                        borderRadius: "4px",
                      }}
                    >
                      Split Applicant
                    </Button>
                  </Tooltip>
                )}

                <Tooltip title="Download Rental Verification Form">
                  <Button
                    type="primary"
                    size="large"
                    icon={<DownloadOutlined />}
                    onClick={handleVerificationForm}
                    loading={downloading}
                    className={`${styles.animatedButton} ${
                      downloading ? styles.downloading : ""
                    }`}
                    style={{
                      fontWeight: "500",
                      fontSize: "16px",
                      fontFamily: "Lato",
                      borderRadius: "4px",
                    }}
                  >
                    {downloading ? "Preparing..." : "Verification Form"}
                  </Button>
                </Tooltip>

                <Tooltip title="Download Group Data PDF">
                  <Button
                    type="primary"
                    size="large"
                    icon={<DownloadOutlined />}
                    onClick={handleGeneratePDF}
                    style={{
                      fontWeight: "500",
                      fontSize: "16px",
                      fontFamily: "Lato",
                      borderRadius: "4px",
                    }}
                  >
                    Group PDF
                  </Button>
                </Tooltip>
              </Space>
            </div>
            <div className={styles.screeningSection}>{renderFetchButton()}</div>
            <div className={styles.screeningSection}>
              {renderSubmitButton()}
            </div>
          </div>
          <p className={styles.screeningStatus} style={{ margin: 0 }}>
            {renderStatusMessage()}
          </p>
        </div>
      </div>

      {/* Group Summary Card - Redesigned compact layout */}
      <div className={styles.card}>
        <div className={styles.application}>
          <p className={styles.name}>Group Summary</p>

          {/* Main content grid - 2 columns for primary info */}
          <div className={styles.summaryGrid}>
            {/* Left Column: Recommendations & Verification */}
            <div className={styles.summaryColumn}>
              {/* Transunion Results Section */}
              <div className={styles.summarySection}>
                <RecommendationField
                  title="Transunion Recommendation"
                  screening_data={selectedAppGroupScreening}
                  applicant_names={applicant_names}
                  get_recommendation={(screening: any) => ({
                    recommendation: screening.ApplicationRecommendation,
                    message: screening.ApplicationRecommendationMessage,
                  })}
                  useApplicationLevel={true}
                  tooltip={
                    <div>
                      <div style={{ marginBottom: '8px' }}>
                        Visit TransUnion TrueVision to view detailed resident screening reports.
                      </div>
                      {selectedAppGroupScreening?.ApplicationNumber && (
                        <a 
                          href={`https://residentscreening.transunion.com/Result/${selectedAppGroupScreening.ApplicationNumber}?redirect=true`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#1890ff', fontWeight: 500 }}
                        >
                          View in TrueVision Dashboard →
                        </a>
                      )}
                    </div>
                  }
                />

                {/* Security Deposit Recommendation */}
                <div className={styles.subSection}>
                  <p className={styles.detail}>Security Deposit Recommendation</p>
                  {(() => {
                    const applicantsData = selectedAppGroupScreening?.Applicants;
                    
                    if (!applicantsData?.length) {
                      return (
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <FaCircle style={{ color: "gray", height: "12px" }} />
                          <p>Not-Started</p>
                        </div>
                      );
                    }

                    const scores = applicantsData
                      .map((applicant: any) => Number(applicant?.ScoreResult?.ApplicantScore))
                      .filter((score: number) => !isNaN(score) && score > 0);

                    if (scores.length === 0) {
                      return (
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <FaCircle style={{ color: "gray", height: "12px" }} />
                          <p>No score available</p>
                        </div>
                      );
                    }

                    const avgScore = Math.round(scores.reduce((sum: number, score: number) => sum + score, 0) / scores.length);
                    let recommendation: string;
                    let color: string;
                    
                    if (avgScore > 629) {
                      recommendation = "Fully Approved - No Additional Deposit Required";
                      color = "green";
                    } else if (avgScore >= 600 && avgScore <= 629) {
                      recommendation = "Additional ½ Month Deposit Required";
                      color = "orange";
                    } else if (avgScore >= 549 && avgScore <= 599) {
                      recommendation = "Additional 1 Month Deposit Required";
                      color = "#d4a017";
                    } else {
                      recommendation = "Denied";
                      color = "red";
                    }

                    return (
                      <>
                        <p style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <FaCircle style={{ color, height: "12px" }} />
                          {recommendation}
                        </p>
                        <p className={styles.subDetail}>
                          Based on avg. TruVision score: {avgScore}
                        </p>
                      </>
                    );
                  })()}
                </div>

                {/* Screening Results Grid - Credit Score, ID, Criminal, Eviction */}
                <div className={styles.screeningResultsGrid}>
                  {/* Credit Score - border color based on whether scores exist */}
                  {(() => {
                    const hasScores = selectedAppGroupScreening?.Applicants?.some(
                      (a: any) => a?.ScoreResult?.ApplicantScore
                    );
                    const borderClass = hasScores ? styles.statusGreen : styles.statusGray;
                    return (
                      <div className={`${styles.screeningItem} ${borderClass}`}>
                        <ValueField
                          title="TruVision Resident Score (Credit Score)"
                          screening_data={selectedAppGroupScreening}
                          applicant_names={applicant_names}
                          getValue={(screening: any) => screening.ScoreResult.ApplicantScore}
                        />
                      </div>
                    );
                  })()}

                  {/* ID Verification - border color based on verification status */}
                  {(() => {
                    const idvStatuses = applicants?.map((applicant: any, index: number) => {
                      // Match by user ID first, fall back to index
                      const userId = applicant?.user;
                      const idvData = userId && data?.idvByUserId?.[userId] 
                        ? data.idvByUserId[userId] 
                        : data?.applicantsIdv?.[index];
                      const isPlaidVerified = idvData?.status === 'success';
                      const isManuallyVerified = applicant?.id_manually_verified === true;
                      const isFailed = idvData?.status === 'failed' && !isManuallyVerified;
                      return { isVerified: isPlaidVerified || isManuallyVerified, isFailed };
                    }) || [];
                    
                    const allVerified = idvStatuses.length > 0 && idvStatuses.every((s: any) => s.isVerified);
                    const anyFailed = idvStatuses.some((s: any) => s.isFailed);
                    const borderClass = allVerified ? styles.statusGreen : anyFailed ? styles.statusRed : styles.statusGray;

                    // Helper function to copy client user ID to clipboard
                    const handleCopyClientUserId = (userId: string, firstName: string) => {
                      navigator.clipboard.writeText(userId).then(() => {
                        message.success(`Copied ${firstName}'s Client User ID`);
                      }).catch(() => {
                        message.error('Failed to copy');
                      });
                    };
                    
                    return (
                      <div className={`${styles.screeningItem} ${borderClass}`}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Tooltip 
                            title={
                              <div>
                                <div style={{ marginBottom: '8px' }}>
                                  Visit the Plaid dashboard to view ID Verification details.
                                </div>
                                {data?.applicantsIdv?.[0]?.id && (
                                  <a 
                                    href={`https://dashboard.plaid.com/identity_verification/sessions/${data.applicantsIdv[0].id}?status=${data.applicantsIdv[0].status || ''}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#1890ff', fontWeight: 500 }}
                                  >
                                    View IDV in Plaid Dashboard →
                                  </a>
                                )}
                              </div>
                            }
                          >
                            <p className={styles.detail} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', margin: 0 }}>
                              ID Verification
                              <span className={styles.plaidBadge}>
                                <img src={plaid} alt="Plaid" className={styles.plaidIcon} />
                                Plaid
                              </span>
                            </p>
                          </Tooltip>
                          <Tooltip title="Refresh IDV status from Plaid. Use this if verification was completed but status hasn't updated.">
                            <Button
                              type="text"
                              size="small"
                              icon={<SyncOutlined spin={refreshingIDV} />}
                              onClick={handleRefreshIDV}
                              loading={refreshingIDV}
                              style={{ fontSize: '12px', padding: '2px 8px' }}
                            >
                              {refreshingIDV ? '' : 'Refresh'}
                            </Button>
                          </Tooltip>
                        </div>
                        {applicants?.map((applicant: any, index: number) => {
                          // Match by user ID first, fall back to index
                          const userId = applicant?.user;
                          const idvData = userId && data?.idvByUserId?.[userId] 
                            ? data.idvByUserId[userId] 
                            : data?.applicantsIdv?.[index];
                          const firstName = applicant?.first_name || applicant_names?.[index]?.first_name || 'Unknown';
                          const isPlaidVerified = idvData?.status === 'success';
                          const isManuallyVerified = applicant?.id_manually_verified === true;
                          const isVerified = isPlaidVerified || isManuallyVerified;
                          const isFailed = idvData?.status === 'failed' && !isManuallyVerified;
                          const hasPaid = applicant?.payment_received === true;
                          // The client_user_id is the user ID used for Plaid (same as applicant.user)
                          const clientUserId = userId;

                          // Determine status text: show "Not Started" if applicant hasn't paid yet
                          const getStatusText = () => {
                            if (isVerified) {
                              return isManuallyVerified && !isPlaidVerified ? 'Manually Verified' : 'Verified';
                            }
                            if (isFailed) return 'Failed';
                            if (!hasPaid) return 'Not Started';
                            return 'Pending';
                          };
                          
                          return (
                            <div key={index} className={styles.idvApplicantRow}>
                              <p style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
                                <FaCircle style={{ color: isVerified ? 'green' : isFailed ? 'red' : 'gray' }} />
                                {getStatusText()} for {firstName}
                              </p>
                              {clientUserId && (
                                <div className={styles.clientUserIdRow}>
                                  <Tooltip title="Copy Client User ID to search in Plaid Dashboard">
                                    <span 
                                      className={styles.clientUserId}
                                      onClick={() => handleCopyClientUserId(clientUserId, firstName)}
                                    >
                                      <CopyOutlined className={styles.copyIcon} />
                                      <span className={styles.clientUserIdText}>
                                        {clientUserId.length > 20 
                                          ? `${clientUserId.substring(0, 8)}...${clientUserId.substring(clientUserId.length - 8)}`
                                          : clientUserId
                                        }
                                      </span>
                                    </span>
                                  </Tooltip>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Criminal Record - border color based on recommendations */}
                  {(() => {
                    const criminalRecs = selectedAppGroupScreening?.Applicants?.map(
                      (a: any) => a?.CriminalRecommendation?.toLowerCase()
                    ) || [];
                    const allAccept = criminalRecs.length > 0 && criminalRecs.every((r: string) => r === 'accept');
                    const anyDecline = criminalRecs.some((r: string) => r === 'decline');
                    const anyRefer = criminalRecs.some((r: string) => r === 'refer');
                    const borderClass = allAccept ? styles.statusGreen : anyDecline ? styles.statusRed : anyRefer ? styles.statusOrange : styles.statusGray;
                    
                    return (
                      <div className={`${styles.screeningItem} ${borderClass}`}>
                        <RecommendationField
                          title="Criminal Record"
                          screening_data={selectedAppGroupScreening}
                          applicant_names={applicant_names}
                          get_recommendation={(screening: any) => ({
                            message: screening.CriminalRecommendation,
                          })}
                        />
                      </div>
                    );
                  })()}

                  {/* Eviction Record - border color based on recommendations */}
                  {(() => {
                    const evictionRecs = selectedAppGroupScreening?.Applicants?.map(
                      (a: any) => a?.EvictionRecommendation?.toLowerCase()
                    ) || [];
                    const allAccept = evictionRecs.length > 0 && evictionRecs.every((r: string) => r === 'accept');
                    const anyDecline = evictionRecs.some((r: string) => r === 'decline');
                    const anyRefer = evictionRecs.some((r: string) => r === 'refer');
                    const borderClass = allAccept ? styles.statusGreen : anyDecline ? styles.statusRed : anyRefer ? styles.statusOrange : styles.statusGray;
                    
                    return (
                      <div className={`${styles.screeningItem} ${borderClass}`}>
                        <RecommendationField
                          title="Eviction Record"
                          screening_data={selectedAppGroupScreening}
                          applicant_names={applicant_names}
                          get_recommendation={(screening: any) => ({
                            message: screening.EvictionRecommendation,
                          })}
                        />
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Right Column: Income Information */}
            <div className={styles.summaryColumn}>
              <div className={styles.summarySection}>
                <p className={styles.detail}>Group Income</p>
                {(() => {
                  const totalManualIncome = applicants?.reduce((sum: number, applicant: any) => {
                    return sum + (applicant?.manual_income || 0);
                  }, 0) || 0;
                  const plaidIncome = data?.groupMonthlyIncome || 0;
                  const totalGroupIncome = plaidIncome + totalManualIncome;
                  const hasPlaidIncome = plaidIncome > 0;
                  const hasManualIncome = totalManualIncome > 0;
                  const hasExcludedIncome = data?.groupMonthlyIncome <= 0 &&
                    ((data?.applicantsIncome?.length > 0 &&
                      data.applicantsIncome.some((item: any) => item && !item.error)) ||
                      (data?.applicantsBankIncome?.length > 0 &&
                        data.applicantsBankIncome.some((item: any) => item && !item.error)));
                  
                  return (
                    <>
                      <div className={styles.incomeHeader}>
                        <span className={styles.incomeAmount}>${totalGroupIncome.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo</span>
                        <span className={styles.subDetail}>${(totalGroupIncome * 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/yr</span>
                      </div>

                      {hasPlaidIncome && hasManualIncome ? (
                        <p className={styles.verifiedBadge}>
                          <span className={styles.plaidBadge}>
                            <img src={plaid} alt="Plaid" className={styles.plaidIcon} />
                            Plaid
                          </span>
                          {" + "}
                          <span className={styles.manualBadge}>✏️ Manual</span>
                        </p>
                      ) : hasPlaidIncome ? (
                        <p className={styles.verifiedBadge}>
                          Verified by{" "}
                          <span className={styles.plaidBadge}>
                            <img src={plaid} alt="Plaid" className={styles.plaidIcon} />
                            Plaid
                          </span>
                        </p>
                      ) : hasManualIncome ? (
                        <p className={styles.manualEntryBadge}>
                          <span className={styles.manualBadge}>✏️ Manual entry</span>
                        </p>
                      ) : hasExcludedIncome ? (
                        <p className={styles.unverifiedText}>Income Verified – Excluded</p>
                      ) : (
                        <p className={styles.unverifiedText}>Data has not been verified by Plaid</p>
                      )}

                      {/* Income Confidence Indicator */}
                      {(() => {
                        // Build per-applicant confidence data
                        const applicantsWithLimitedData: Array<{
                          name: string;
                          deposits: number;
                          level: string;
                          requiresVerification: boolean;
                        }> = [];

                        applicants?.forEach((applicant: any, index: number) => {
                          const bankIncomeData = data?.applicantsBankIncome?.[index];
                          const confidence = bankIncomeData?.income_confidence;
                          const firstName = applicant?.first_name || `Applicant ${index + 1}`;
                          
                          // Define high confidence as having 4 or more deposits
                          const transactionCount = confidence?.transaction_count || 0;
                          const isHighConfidence = transactionCount >= 4;
                          
                          // Only include applicants with limited data (not high confidence)
                          if (confidence && !isHighConfidence) {
                            applicantsWithLimitedData.push({
                              name: firstName,
                              deposits: transactionCount,
                              level: confidence.level,
                              requiresVerification: confidence.requires_verification || false,
                            });
                          }
                        });

                        // Don't show warning if no applicants have limited data
                        if (applicantsWithLimitedData.length === 0) {
                          return null;
                        }

                        // Determine overall severity (worst case)
                        const hasVeryLow = applicantsWithLimitedData.some(a => a.level === 'very_low');
                        const hasLow = applicantsWithLimitedData.some(a => a.level === 'low');

                        // Format the applicant names nicely (e.g., "Luke and Elijah" or "Luke, Elijah, and Gage")
                        const names = applicantsWithLimitedData.map(a => a.name);
                        let formattedNames = '';
                        if (names.length === 1) {
                          formattedNames = names[0];
                        } else if (names.length === 2) {
                          formattedNames = `${names[0]} and ${names[1]}`;
                        } else {
                          formattedNames = names.slice(0, -1).join(', ') + ', and ' + names[names.length - 1];
                        }

                        return (
                          <div 
                            className={styles.qualificationIndicator}
                            style={{
                              background: hasVeryLow ? '#fef2f2' : hasLow ? '#fffbeb' : '#fefce8',
                              border: `1px solid ${hasVeryLow ? '#fca5a5' : hasLow ? '#fcd34d' : '#fde047'}`,
                              color: hasVeryLow ? '#991b1b' : hasLow ? '#92400e' : '#854d0e',
                            }}
                          >
                            <div className={styles.qualificationHeader}>
                              ⚠ Limited Income Data for {formattedNames}
                            </div>
                            <div className={styles.qualificationDetails}>
                              Consider verifying with paystubs, offer letters, or bank statements
                            </div>
                          </div>
                        );
                      })()}

                      {/* Income Qualification Indicator */}
                      {(() => {
                        const rentAmount = selectedAppGroup?.leaseDraft?.rent || selectedPropertyUnitRent || 0;
                        const selectedUnitObj = selectedPropertyUnits?.find(
                          (unit: any) => unit.unitNumber === selectedAppGroup?.desired_unit_number
                        );
                        const isAffordableUnit = selectedUnitObj?.isAffordableUnit || false;
                        const incomeMultiple = isAffordableUnit 
                          ? (hasManualIncome ? 2.0 : 1.5)
                          : (hasManualIncome ? 2.5 : 1.7);
                        const incomeType = hasManualIncome ? 'gross' : 'net';
                        const requiredIncome = rentAmount * incomeMultiple;
                        const isQualified = totalGroupIncome >= requiredIncome;
                        
                        if (rentAmount > 0) {
                          return (
                            <div className={`${styles.qualificationIndicator} ${isQualified ? styles.qualified : styles.notQualified}`}>
                              <div className={styles.qualificationHeader}>
                                {isQualified ? '✓' : '✗'} {isQualified ? 'Meets income requirement' : 'Below income requirement'}
                                {isAffordableUnit && <span className={styles.affordableTag}> (Affordable Unit)</span>}
                              </div>
                              <div className={styles.qualificationDetails}>
                                Required: ${requiredIncome.toLocaleString('en-US', { minimumFractionDigits: 2 })}/mo
                                <span className={styles.qualificationFormula}>(${rentAmount.toLocaleString()} × {incomeMultiple} {incomeType})</span>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </>
                  );
                })()}

                {/* Individual Income Breakdown */}
                <div className={styles.incomeBreakdown}>
                  <span className={styles.incomeBreakdownTitle}>By Individual</span>
                  
                  <div className={styles.manualIncomeInfoGlobal}>
                    <span>ℹ️</span>
                    <span>Manual income should be verified with paystubs, offer letter, or bank statements.</span>
                  </div>
                  
                  {applicants?.map((applicant: any, index: number) => {
                    const payrollIncome = data?.applicantsIncome?.[index]?.monthlyIncome;
                    const bankIncomeData = data?.applicantsBankIncome?.[index];
                    const bankIncome = bankIncomeData?.included_income || 0;
                    const excludedIncome = bankIncomeData?.excluded_income || 0;
                    const monthlyIncome = payrollIncome || bankIncome || 0;
                    const skippedIncomeVerification = applicant?.skipped_income_verification === true;
                    const isGuarantor = !skippedIncomeVerification && applicant?.gurantor === true;
                    const firstName = applicant?.first_name || applicant_names?.[index]?.first_name || 'Unknown';
                    const applicationId = applicant?.application_id;
                    const savedManualIncome = applicant?.manual_income;
                    
                    const deduplicationInfo = data?.incomeDeduplication?.[index];
                    const isDeduplicatedIncome = (deduplicationInfo?.bankDeduplicatedFrom !== null && deduplicationInfo?.bankDeduplicatedFrom !== undefined) ||
                      (deduplicationInfo?.payrollDeduplicatedFrom !== null && deduplicationInfo?.payrollDeduplicatedFrom !== undefined);
                    const deduplicatedFromIndex = deduplicationInfo?.bankDeduplicatedFrom ?? deduplicationInfo?.payrollDeduplicatedFrom;
                    const deduplicatedFromName = deduplicatedFromIndex !== null && deduplicatedFromIndex !== undefined
                      ? (applicants?.[deduplicatedFromIndex]?.first_name || 'Applicant 1')
                      : null;
                    
                    const hasExcludedIncome = excludedIncome > 0;
                    const hasVerifiedIncome = monthlyIncome > 0;
                    const hasManualIncome = savedManualIncome > 0;
                    const totalApplicantIncome = monthlyIncome + (savedManualIncome || 0);
                    
                    const getIncomeLabel = () => {
                      if (hasVerifiedIncome && hasManualIncome) return 'Plaid + Manual';
                      if (hasManualIncome) return 'Manual';
                      if (hasVerifiedIncome) return 'Plaid';
                      return null;
                    };
                    
                    // Get income confidence data for this applicant
                    const incomeConfidence = bankIncomeData?.income_confidence;
                    const transactionCount = incomeConfidence?.transaction_count || 0;
                    const daysOfData = incomeConfidence?.days_of_data || 0;
                    const hasIncomeData = transactionCount > 0 || daysOfData > 0;
                    const isLowConfidence = transactionCount < 4;
                    
                    return (
                      <div key={index} className={styles.incomeItemCompact}>
                        <div className={styles.incomeMainRow}>
                          <div className={styles.incomeNameCol}>
                            <span className={styles.applicantName}>{firstName}</span>
                            {isGuarantor && <span className={styles.tagSmall}>🛡️ Guarantor</span>}
                            {/* Income confidence badge */}
                            {hasIncomeData && !skippedIncomeVerification && !isDeduplicatedIncome && (
                              <span 
                                style={{
                                  fontSize: '10px',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  marginLeft: '6px',
                                  fontWeight: 500,
                                  background: isLowConfidence ? '#fef3c7' : '#dcfce7',
                                  color: isLowConfidence ? '#92400e' : '#166534',
                                  border: `1px solid ${isLowConfidence ? '#fcd34d' : '#86efac'}`,
                                }}
                                title={`${transactionCount} deposits over ${daysOfData} days`}
                              >
                                {isLowConfidence && '⚠ '}{transactionCount > 5 ? '5+' : transactionCount} dep • {daysOfData}d
                              </span>
                            )}
                          </div>
                          <div className={styles.incomeAmountCol}>
                            {totalApplicantIncome > 0 ? (
                              <>
                                <span className={hasManualIncome ? styles.amountManual : styles.amountPlaid}>
                                  ${totalApplicantIncome.toLocaleString('en-US', { minimumFractionDigits: 2 })}/mo
                                </span>
                                <span className={styles.sourceTag}>{getIncomeLabel()}</span>
                              </>
                            ) : isDeduplicatedIncome ? (
                              <span className={styles.statusMuted}>Joint w/ {deduplicatedFromName}</span>
                            ) : skippedIncomeVerification ? (
                              <span className={styles.statusMuted}>Will use guarantor/roommate</span>
                            ) : (
                              <span className={styles.statusMuted}>No data</span>
                            )}
                          </div>
                        </div>
                        
                        {hasExcludedIncome && !hasManualIncome && (
                          <div className={styles.excludedNotice}>
                            ${excludedIncome.toLocaleString()} excluded by Plaid
                          </div>
                        )}
                        
                        {applicationId && (
                          <div className={styles.manualInputSection}>
                            <div 
                              className={styles.manualInputToggle}
                              onClick={() => setExpandedIncomeInputs(prev => ({ 
                                ...prev, 
                                [applicationId]: !prev[applicationId] 
                              }))}
                            >
                              <span className={styles.manualInputLabel}>
                                <EditOutlined style={{ marginRight: 4 }} />
                                {hasManualIncome ? 'Edit manual income' : 'Add manual income'}
                              </span>
                              {expandedIncomeInputs[applicationId] ? (
                                <UpOutlined className={styles.toggleIcon} />
                              ) : (
                                <DownOutlined className={styles.toggleIcon} />
                              )}
                            </div>
                            {expandedIncomeInputs[applicationId] && (
                              <div className={styles.manualInputRow}>
                                <InputNumber
                                  prefix="$"
                                  placeholder={savedManualIncome ? String(savedManualIncome) : "0.00"}
                                  min={0}
                                  size="small"
                                  style={{ width: 110 }}
                                  value={manualIncomeValues[applicationId]}
                                  onChange={(value) => setManualIncomeValues(prev => ({ ...prev, [applicationId]: value }))}
                                  formatter={(value) => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                                  parser={(value) => value ? parseFloat(value.replace(/\$\s?|(,*)/g, '')) : 0}
                                />
                                <span className={styles.perMonth}>/mo</span>
                                <Button
                                  type="primary"
                                  size="small"
                                  loading={savingManualIncome[applicationId]}
                                  disabled={manualIncomeValues[applicationId] === undefined && !savedManualIncome}
                                  onClick={() => handleSaveManualIncome(applicationId, manualIncomeValues[applicationId] ?? savedManualIncome)}
                                >
                                  Save
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Property Details Row */}
          <div className={styles.propertyDetailsRow}>
            <div className={styles.propertyItem}>
              <p className={styles.detail}>Desired property</p>
              <Select
                style={{ cursor: "pointer", width: "100%" }}
                placeholder="Select a property"
                value={selectedProperty}
                onChange={(value) => handleChange("property", value)}
                optionFilterProp="label"
                filterOption={(input: string, option: any) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {propertyOptions().map((option: any) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </div>

            <div className={styles.propertyItem}>
              <p className={styles.detail}>Desired apartment</p>
              <Select
                style={{ cursor: "pointer", width: "100%" }}
                placeholder="Select a unit"
                value={selectedUnit}
                onChange={(value) => handleChange("apartment", value)}
                optionFilterProp="children"
                filterOption={(input: string, option: any) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {(selectedPropertyUnits || []).map((unit: any) => (
                  <Option key={unit.unitId} value={unit.unitNumber}>
                    {unit.unitNumber}
                  </Option>
                ))}
              </Select>
            </div>

            <div className={styles.propertyItem}>
              <p className={styles.detail}>Desired apartment rent</p>
              <p style={{ display: "flex", gap: 10, alignItems: "center" }}>
                {selectedAppGroup?.leaseDraft?.rent !== undefined ? (
                  `$ ${selectedAppGroup.leaseDraft.rent}`
                ) : (
                  <>
                    $ {selectedPropertyUnitRent || "0"}
                    {loadingRent && <Spin size="small" style={{ color: "#1677ff" }} />}
                  </>
                )}
              </p>
              {selectedAppGroup?.leaseDraft?.rent !== undefined && (
                <p className={styles.subDetail}>
                  $ {selectedPropertyUnitRent || "0"} (Actual rent)
                  {loadingRent && <Spin size="small" style={{ color: "#1677ff" }} />}
                </p>
              )}
            </div>

            <div className={styles.propertyItem}>
              <p className={styles.detail}>Desired lease term</p>
              <p>{typeof desired_lease_term === "number" ? desired_lease_term : desired_lease_term}</p>
            </div>

            <div className={styles.propertyItem}>
              <p className={styles.detail}>Desired move in date</p>
              <p>{desired_move_in_date ? formatDate(desired_move_in_date) : "Not specified"}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Applicant Tabs */}
      <Tabs defaultActiveKey="1" items={tabItems} onChange={handleTabClick} />

      {/* Split Application Modal */}
      <SplitApplicationModal
        visible={splitModalVisible}
        onClose={() => setSplitModalVisible(false)}
        onSuccess={async () => {
          // Refresh the application group data
          if (selectedAppGroupId) {
            await dispatch(asyncGetApplicationsByGroup(selectedAppGroupId));
            // Also refresh the main list
            const userInfo: any = user?.user;
            if (userInfo?.localAccountId) {
              await dispatch(asyncGetApplicationGroups(userInfo.localAccountId));
            }
          }
        }}
        applicants={applicants || []}
        applicationGroupId={selectedAppGroupId || ''}
      />
    </div>
  );
};

export default GroupData;
